import { createContext, useCallback, useContext, useState } from "react";

import { T } from "./translations.js";
// The ONE list of languages 3D Maps speaks: viewer selector, admin content
// tabs, the venue's default language. Item 0374 found the admin on its own
// five-language copy while the viewer offered eight. Never inline another list;
// import LANGS / LANG_CODES. backend/app/i18n.py SUPPORTED must hold the same
// codes (backend/tests/test_languages.py fails when they drift).
import LANGUAGES from "./languages.json";

/** [[code, native name], ...] in display order. */
export const LANGS = LANGUAGES;
export const LANG_CODES = LANGUAGES.map(([code]) => code);
const isLang = (code) => LANG_CODES.includes(code);

const KEY = "promomaps_lang";

// Used when nothing is stored and the browser's language isn't one we support.
// NOT the missing-key fallback — that stays English (see t()/tr() below).
const FALLBACK = "es";

function detect() {
  // ?lang= wins: it's how a kiosk is provisioned to boot in a fixed language.
  try {
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && isLang(param)) return param;
  } catch {
    /* ignore */
  }
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && isLang(saved)) return saved;
  } catch {
    /* private mode / storage disabled */
  }
  const nav = (navigator.language || FALLBACK).slice(0, 2).toLowerCase();
  return isLang(nav) ? nav : FALLBACK;
}

// Module-level mirror so non-React code (api.js's Accept-Language header, the
// map's localized labels) can read the language without the hook.
let activeLang = detect();
export const getLang = () => activeLang;

/** Translate outside React (api.js error messages). Same fallback chain as t(). */
export function tr(key, vars) {
  let text = T[activeLang]?.[key] ?? T.en?.[key] ?? key;
  if (vars) {
    for (const name in vars) {
      text = text.split("{" + name + "}").join(vars[name]);
    }
  }
  return text;
}

// The venue's own language (bundle settings.default_lang), set by the viewer
// when it boots. pickName() tries it before falling back to "whatever key
// comes first", so a name never depends on the order its map was stored in
// (item 0375: a snapshot that sorted keys turned "Playa de Levante" into
// "Platja de Llevant" for every visitor without es/en text).
let contentLang = null;
export function setContentLang(code) {
  contentLang = isLang(code) ? code : null;
}

/** Resolve a translatable-content map ({en, es, ...}) from the API: the
 *  visitor's language, else English, else the venue's default language, else
 *  the first language that has text. Blank strings count as missing (item
 *  0374): with eight admin tabs an editor can leave "" behind in one, and that
 *  must fall back rather than render an empty name. English stays ahead of the
 *  venue language because that is what every visitor saw before the venue
 *  language was added here; the backend's i18n.pick() (assistant only) puts
 *  the venue language first. */
export function pickName(content, lang = activeLang) {
  if (!content) return "";
  const has = (v) => typeof v === "string" && v.trim() !== "";
  if (has(content[lang])) return content[lang];
  if (has(content.en)) return content.en;
  if (contentLang && has(content[contentLang])) return content[contentLang];
  return Object.values(content).find(has) ?? "";
}

const Ctx = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detect);

  const setLang = useCallback((next) => {
    if (!isLang(next)) return;
    activeLang = next;
    setLangState(next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = next;
  }, []);

  // t(key, vars?) — falls back to English, then the raw key. {var} interpolation.
  const t = useCallback(
    (key, vars) => {
      let text = T[lang]?.[key] ?? T.en?.[key] ?? key;
      if (vars) {
        for (const name in vars) {
          text = text.split("{" + name + "}").join(vars[name]);
        }
      }
      return text;
    },
    [lang],
  );

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export function useT() {
  return useContext(Ctx) ?? { lang: FALLBACK, setLang: () => {}, t: (key) => key };
}
